import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import logo from '../assets/images/logo.png';
import heroBackground from '../assets/images/hero-background.png';
import { getPostBySlug } from '../data/blogPosts';
import { usePageSeo } from '../hooks/usePageSeo';

function formatInline(text) {
  if (!text) return null;
  const segments = text.split(/(\*\*.+?\*\*)/g);
  return segments.map((seg, i) => {
    const m = seg.match(/^\*\*(.+)\*\*$/);
    if (m) {
      return (
        <strong key={i} className="font-semibold text-white/95">
          {m[1]}
        </strong>
      );
    }
    return <span key={i}>{seg}</span>;
  });
}

function renderBlocks(blocks) {
  return blocks.map((block, i) => {
    if (block.type === 'p') {
      return (
        <p key={i} className="text-sm leading-relaxed text-white/80 sm:text-base">
          {formatInline(block.text)}
        </p>
      );
    }
    if (block.type === 'h2') {
      return (
        <h2 key={i} className="pt-2 text-2xl font-semibold text-[#486FFF] sm:text-3xl">
          {block.text}
        </h2>
      );
    }
    if (block.type === 'h3') {
      return (
        <h3 key={i} className="pt-1 text-xl font-semibold text-white/90 sm:text-2xl">
          {block.text}
        </h3>
      );
    }
    if (block.type === 'ul' && Array.isArray(block.items)) {
      return (
        <ul key={i} className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/80 sm:text-base">
          {block.items.map((item, j) => (
            <li key={j}>{formatInline(item)}</li>
          ))}
        </ul>
      );
    }
    return null;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { user } = useAuth();
  const post = useMemo(() => getPostBySlug(slug), [slug]);

  const canonicalPath = post ? `/blog/${post.slug}` : '';

  usePageSeo({
    title: post?.title,
    description: post?.description,
    keywords: post?.keywords,
    canonicalPath
  });

  const jsonLd = useMemo(() => {
    if (!post) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      datePublished: post.datePublished,
      dateModified: post.dateModified || post.datePublished,
      author: { '@type': 'Organization', name: post.author || 'MyRDP' },
      publisher: { '@type': 'Organization', name: 'MyRDP' },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': origin ? `${origin}/blog/${post.slug}` : `/blog/${post.slug}`
      },
      keywords: post.keywords.join(', ')
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-[#050613] text-white bg-no-repeat bg-contain bg-center-top"
      style={{ backgroundImage: `url(${heroBackground})` }}
    >
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      <Header user={user} logo={logo} />

      <main className="px-4 pt-10 pb-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <nav className="mx-auto mb-8 max-w-4xl text-xs text-white/50 sm:text-sm" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="hover:text-white">
                  Home
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                <Link to="/blog" className="hover:text-white">
                  Blog
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li className="text-white/70 line-clamp-1">{post.title}</li>
            </ol>
          </nav>

          <article
            className="mx-auto max-w-4xl animate-fadeInSoft rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF10] p-6 backdrop-blur-sm sm:p-10"
            itemScope
            itemType="https://schema.org/BlogPosting"
          >
            <meta itemProp="datePublished" content={post.datePublished} />
            <meta itemProp="dateModified" content={post.dateModified || post.datePublished} />
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] tracking-widest text-white/75">
              {post.readTime}
            </span>
            <h1 className="mt-4 text-3xl font-medium leading-tight text-white/95 sm:text-4xl md:text-[2.5rem]" itemProp="headline">
              {post.title}
            </h1>
            <p className="mt-2 text-sm text-white/55" itemProp="description">
              By {post.author} ·{' '}
              <time dateTime={post.datePublished}>
                {new Date(post.datePublished + 'T12:00:00').toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </p>

            <div className="mt-8 space-y-6" itemProp="articleBody">
              {renderBlocks(post.blocks)}
            </div>

            <div className="mt-10 border-t border-white/10 pt-8">
              <p className="text-xs font-medium uppercase tracking-wider text-white/45">Topics &amp; keywords</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {post.keywords.map((kw) => (
                  <span
                    key={kw}
                    className="rounded-full border border-[#486FFF]/30 bg-[#486FFF]/10 px-3 py-1 text-xs text-[#b8c9ff]"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/blog"
                className="inline-flex rounded-full border border-white/20 px-5 py-2.5 text-sm text-white/90 hover:bg-white/5"
              >
                ← All articles
              </Link>
              <Link
                to="/signup"
                className="inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-medium text-[#050613] hover:bg-white/90"
              >
                Get started with MyRDP
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer user={user} />
    </div>
  );
}
